import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import Decimal from 'decimal.js';
import React, { memo } from 'react';
import { useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import ControlledCheckBox from '../../components/BasicUI/ControlledInputs/ControlledCheckBox';
import ControlledTextField from '../../components/BasicUI/ControlledInputs/ControlledTextField';
import { formatMoney } from '../../util/currency';

const { Money } = sdkTypes;

const CUSTOM_LINE_ITEM_MAX_COUNT = 10;

const DEFAULT_QUANTITY = 1;

const getTypeLabel = (type, intl) => {
  const perItem = type?.perItem
    ? intl.formatMessage({ id: 'BookingDatesForm.customLineItem.perItem' })
    : null;
  const perNight = type?.perNight
    ? intl.formatMessage({ id: 'BookingDatesForm.customLineItem.perNight' })
    : null;
  const perGuest = type?.perGuest
    ? intl.formatMessage({ id: 'BookingDatesForm.customLineItem.perGuest' })
    : null;
  const and = intl.formatMessage({ id: 'BookingDatesForm.customLineItem.and' });

  let label = '';
  if (perItem) {
    label += `/${perItem}`;
  }
  if (perNight && !perGuest) {
    label += `/${perNight}`;
  }
  if (perGuest && !perNight) {
    label += `/${perGuest}`;
  }
  if (perGuest && perNight) {
    label += `/${perGuest} ${and} ${perNight}`;
  }
  return label;
};

const CustomLineItemsStepComponent = ({
  control,
  customLineItems,
  disabled = false,
}) => {
  const intl = useIntl();

  // Watch all customLineItems form values for reactivity
  const formCustomLineItems = useWatch({ control, name: 'customLineItems' }) || {};

  const customLineItemKeys = Object.keys(customLineItems);

  if (customLineItemKeys.length === 0) {
    return null;
  }

  const infoText = intl.formatMessage({ id: 'BookingDatesForm.customLineItemsStepInfo' });

  return (
    <Stack direction="column" spacing={1}>
      <Typography color="text.secondary" variant="body2">
        {infoText}
      </Typography>
      <Stack direction="column" spacing={0}>
        {customLineItemKeys.map((key) => {
          const item = customLineItems[key];
          const price = new Money(
            new Decimal(item.price.amount).round().toNumber(),
            item.price.currency,
          );
          const formattedPrice = formatMoney(intl, price);
          const typeLabel = getTypeLabel(item.type, intl);
          const isChecked = formCustomLineItems[key]?.add;
          const showQuantityInput = isChecked && item.type?.perItem;

          const label = (
            <span>
            {`${item.title} - ${formattedPrice}`}
              <Typography color="text.secondary" component="span" variant="body2">
              {' '}{typeLabel}
            </Typography>
          </span>
          );

          return (
            <Stack direction="row" key={key} width="100%">
              <ControlledCheckBox
                control={control}
                disabled={disabled}
                label={label}
                name={`customLineItems.${key}.add`}
              />
              {showQuantityInput ? (
                <Box ml={4} mt={1}>
                  <ControlledTextField
                    control={control}
                    controllerOptions={{ defaultValue: DEFAULT_QUANTITY }}
                    label={intl.formatMessage({ id: 'BookingDatesForm.customLineItem.quantityLabel' })}
                    name={`customLineItems.${key}.quantity`}
                    textFieldProps={{
                      disabled,
                      inputProps: {
                        min: 1,
                        max: CUSTOM_LINE_ITEM_MAX_COUNT,
                      },
                      sx: { width: 100 },
                      type: 'number',
                    }}
                  />
                </Box>
              ) : null}
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

const CustomLineItemsStep = memo(CustomLineItemsStepComponent);

export default CustomLineItemsStep;
